import type { ReactNode } from 'react';
import {
  Animated,
  Pressable,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import type { useTheme } from './theme';
import { useAdvanceFlash } from './useAdvanceFlash';

// One tappable timeline set-row card, extracted from TimelineList so it can own
// a per-row flash hook (2026-07-20). A just-logged FAILED set flashes this card
// briefly — border + background pulse via useAdvanceFlash (the same one-shot the
// FocusPanel uses on advance) — instead of the old persistent danger banner: a
// fail is a normal outcome, so it gets the ordinary "here's what landed" flash,
// not an alarm. `flashKey` is 0 for every row except the just-failed one, which
// receives a bumping key so the hook re-fires (Rules of Hooks: each row is its
// own component instance, so the hook count stays stable across the feed).
//
// The card's resting colours are surface / border — exactly useAdvanceFlash's
// rest values — so a non-flashing row (flashKey 0, hook skips its effect) renders
// identically to before the extraction. Press feedback (surface2) and the
// grouped left-stripe are preserved via the Pressable's render-child.
export function TimelineSetRow({
  onPress,
  grouped,
  flashKey,
  palette,
  rowStyle,
  groupedStyle,
  pressedStyle,
  children,
}: {
  onPress: () => void;
  grouped: boolean;
  /** 0 = never flashed; a bumping positive number = this is the just-failed
   *  row, flash now (and again if it re-fires). */
  flashKey: number;
  palette: ReturnType<typeof useTheme>['palette'];
  rowStyle: StyleProp<ViewStyle>;
  groupedStyle: StyleProp<ViewStyle>;
  pressedStyle: StyleProp<ViewStyle>;
  children: ReactNode;
}) {
  const { flashBg, flashBorder } = useAdvanceFlash(flashKey, palette);
  return (
    <Pressable accessibilityRole="button" onPress={onPress}>
      {({ pressed }) => (
        <Animated.View
          style={[
            rowStyle,
            grouped && groupedStyle,
            { backgroundColor: flashBg, borderColor: flashBorder },
            pressed && pressedStyle,
          ]}
        >
          {children}
        </Animated.View>
      )}
    </Pressable>
  );
}
